import { zodResolver } from '@hookform/resolvers/zod';
import {
  useListDashboards,
  useCreateDashboard,
  useDeleteDashboard,
  useDuplicateDashboard,
  useSetHomeDashboard,
  useUpdateDashboard,
  useRenameDashboard,
  getListDashboardsQueryKey,
  type DashboardsDashboardSummary,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Plus,
  RefreshCw,
  AlertCircle,
  Home,
  Copy,
  Trash2,
  Share2,
  Lock,
  MoreVertical,
  Download,
  Upload,
  Pencil,
} from 'lucide-react';
import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Badge, Button, Dropdown, IconButton, Modal, TextField, useToast } from '@/components/ui';
import { ExportAllModal, ImportModal } from '@/features/dashboards/modals';

const createDashboardSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(255).optional(),
});

type CreateDashboardForm = z.infer<typeof createDashboardSchema>;

const renameDashboardSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(255).optional(),
});

type RenameDashboardForm = z.infer<typeof renameDashboardSchema>;

export function DashboardsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [deleteConfirmDashboard, setDeleteConfirmDashboard] =
    useState<DashboardsDashboardSummary | null>(null);
  const [renameDashboard, setRenameDashboard] = useState<DashboardsDashboardSummary | null>(null);

  const { data, isLoading, isError, refetch, isFetching } = useListDashboards({
    query: {
      refetchOnWindowFocus: true,
    },
  });

  const dashboards = data?.dashboards ?? [];
  const ownedDashboards = dashboards.filter((dashboard) => dashboard.isEditable);
  const sharedDashboards = dashboards.filter((dashboard) => !dashboard.isEditable);
  const homeDashboardId = data?.homeDashboardId;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateDashboardForm>({
    resolver: zodResolver(createDashboardSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const {
    register: registerRename,
    handleSubmit: handleSubmitRename,
    reset: resetRename,
    formState: { errors: renameErrors, isSubmitting: isRenameSubmitting },
  } = useForm<RenameDashboardForm>({
    resolver: zodResolver(renameDashboardSchema),
  });

  useEffect(() => {
    if (renameDashboard) {
      resetRename({
        name: renameDashboard.name ?? '',
        description: renameDashboard.description ?? '',
      });
    }
  }, [renameDashboard, resetRename]);

  const { mutate: createDashboard } = useCreateDashboard({
    mutation: {
      onSuccess: (response) => {
        addToast({ message: 'Dashboard created', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
        setIsCreateModalOpen(false);
        reset();
        if (response.dashboard?.id) {
          navigate(`/dashboards/${response.dashboard.id}/edit`);
        }
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to create dashboard',
          variant: 'error',
        });
      },
    },
  });

  const { mutate: deleteDashboard, isPending: isDeleting } = useDeleteDashboard({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Dashboard deleted', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
        setDeleteConfirmDashboard(null);
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to delete dashboard',
          variant: 'error',
        });
      },
    },
  });

  const { mutate: duplicateDashboard } = useDuplicateDashboard({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Dashboard duplicated', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to duplicate dashboard',
          variant: 'error',
        });
      },
    },
  });

  const { mutate: setHomeDashboard } = useSetHomeDashboard({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Home dashboard updated', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to set home dashboard',
          variant: 'error',
        });
      },
    },
  });

  const { mutate: updateDashboard } = useUpdateDashboard({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Dashboard updated', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to update dashboard',
          variant: 'error',
        });
      },
    },
  });

  const onSubmitCreate = useCallback(
    (formData: CreateDashboardForm) => {
      createDashboard({
        data: {
          name: formData.name,
          description: formData.description || undefined,
        },
      });
    },
    [createDashboard]
  );

  const handleSetHome = useCallback(
    (dashboard: DashboardsDashboardSummary) => {
      if (dashboard.id) {
        setHomeDashboard({ data: { dashboardId: dashboard.id } });
      }
    },
    [setHomeDashboard]
  );

  const handleDuplicate = useCallback(
    (dashboard: DashboardsDashboardSummary) => {
      if (dashboard.id) {
        duplicateDashboard({ id: dashboard.id });
      }
    },
    [duplicateDashboard]
  );

  const handleDelete = useCallback(
    (dashboard: DashboardsDashboardSummary) => {
      if (dashboard.id) {
        deleteDashboard({ id: dashboard.id });
      }
    },
    [deleteDashboard]
  );

  const handleToggleShare = useCallback(
    (dashboard: DashboardsDashboardSummary) => {
      if (dashboard.id && dashboard.name) {
        updateDashboard({
          id: dashboard.id,
          data: {
            name: dashboard.name,
            description: dashboard.description || undefined,
            isShared: !dashboard.isShared,
          },
        });
      }
    },
    [updateDashboard]
  );

  const { mutate: renameDashboardMutate } = useRenameDashboard({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Dashboard renamed', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
        setRenameDashboard(null);
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to rename dashboard',
          variant: 'error',
        });
      },
    },
  });

  const onSubmitRename = useCallback(
    (formData: RenameDashboardForm) => {
      if (!renameDashboard?.id) return;
      renameDashboardMutate({
        id: renameDashboard.id,
        data: {
          name: formData.name,
          description: formData.description || undefined,
        },
      });
    },
    [renameDashboard, renameDashboardMutate]
  );

  const handleNavigate = useCallback(
    (dashboard: DashboardsDashboardSummary) => {
      if (dashboard.id) {
        navigate(ROUTES.DASHBOARD_DETAIL.replace(':id', dashboard.id));
      }
    },
    [navigate]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Dashboards
          </h1>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            {isLoading
              ? 'Loading...'
              : `${ownedDashboards.length + sharedDashboards.length} dashboard${ownedDashboards.length + sharedDashboards.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setIsImportModalOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Import
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIsExportModalOpen(true)}>
            <Download className="mr-2 h-4 w-4" />
            Export All
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Dashboard
          </Button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
            <AlertCircle className="h-6 w-6 text-red-400" />
          </div>
          <p className="text-sm text-gray-600 dark:text-zinc-400">Failed to load dashboards</p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-blue-500 hover:text-blue-400"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* My Dashboards */}
          <DashboardSection
            title="My Dashboards"
            dashboards={ownedDashboards}
            homeDashboardId={homeDashboardId}
            isOwned
            onNavigate={handleNavigate}
            onSetHome={handleSetHome}
            onDuplicate={handleDuplicate}
            onDelete={(d) => setDeleteConfirmDashboard(d)}
            onToggleShare={handleToggleShare}
            onRename={(d) => setRenameDashboard(d)}
          />

          {/* Shared Dashboards */}
          {sharedDashboards.length > 0 && (
            <DashboardSection
              title="Shared with Me"
              dashboards={sharedDashboards}
              homeDashboardId={homeDashboardId}
              isOwned={false}
              onNavigate={handleNavigate}
              onSetHome={handleSetHome}
              onDuplicate={handleDuplicate}
            />
          )}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          reset();
        }}
        title="Create Dashboard"
        subtitle="Create a new custom dashboard"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreateModalOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" form="create-dashboard-form" loading={isSubmitting}>
              Create
            </Button>
          </>
        }
      >
        <form id="create-dashboard-form" onSubmit={handleSubmit(onSubmitCreate)}>
          <div className="space-y-4">
            <TextField
              label="Name"
              placeholder="Living Room Overview"
              error={errors.name?.message}
              {...register('name')}
            />
            <TextField
              label="Description"
              placeholder="Optional description"
              error={errors.description?.message}
              {...register('description')}
            />
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirmDashboard}
        onClose={() => setDeleteConfirmDashboard(null)}
        title="Delete Dashboard"
        subtitle={`Are you sure you want to delete "${deleteConfirmDashboard?.name}"? This action cannot be undone.`}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteConfirmDashboard(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={isDeleting}
              onClick={() => deleteConfirmDashboard && handleDelete(deleteConfirmDashboard)}
            >
              Delete
            </Button>
          </>
        }
      >
        <div className="flex items-center gap-3 rounded-lg bg-red-50 px-3 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p>This will permanently delete the dashboard and all its widgets.</p>
        </div>
      </Modal>

      {/* Rename Modal */}
      <Modal
        isOpen={!!renameDashboard}
        onClose={() => setRenameDashboard(null)}
        title="Rename Dashboard"
        subtitle="Update the name and description of this dashboard"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenameDashboard(null)}>
              Cancel
            </Button>
            <Button type="submit" form="rename-dashboard-form" loading={isRenameSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <form id="rename-dashboard-form" onSubmit={handleSubmitRename(onSubmitRename)}>
          <div className="space-y-4">
            <TextField
              label="Name"
              placeholder="Living Room Overview"
              error={renameErrors.name?.message}
              {...registerRename('name')}
            />
            <TextField
              label="Description"
              placeholder="Optional description"
              error={renameErrors.description?.message}
              {...registerRename('description')}
            />
          </div>
        </form>
      </Modal>

      {/* Export All Modal */}
      <ExportAllModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} />

      {/* Import Modal */}
      <ImportModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} />
    </div>
  );
}

interface DashboardSectionProps {
  title: string;
  dashboards: DashboardsDashboardSummary[];
  homeDashboardId?: string;
  isOwned: boolean;
  onNavigate: (dashboard: DashboardsDashboardSummary) => void;
  onSetHome: (dashboard: DashboardsDashboardSummary) => void;
  onDuplicate: (dashboard: DashboardsDashboardSummary) => void;
  onDelete?: (dashboard: DashboardsDashboardSummary) => void;
  onToggleShare?: (dashboard: DashboardsDashboardSummary) => void;
  onRename?: (dashboard: DashboardsDashboardSummary) => void;
}

function DashboardSection({
  title,
  dashboards,
  homeDashboardId,
  isOwned,
  onNavigate,
  onSetHome,
  onDuplicate,
  onDelete,
  onToggleShare,
  onRename,
}: DashboardSectionProps) {
  if (dashboards.length === 0 && isOwned) {
    return (
      <div>
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          {title}
        </h2>
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-800">
            <LayoutDashboard className="h-6 w-6 text-gray-500 dark:text-zinc-500" />
          </div>
          <p className="text-sm text-gray-600 dark:text-zinc-400">No dashboards yet</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">
            Create your first dashboard to get started
          </p>
        </div>
      </div>
    );
  }

  if (dashboards.length === 0) return null;

  return (
    <div>
      <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
        {title}
      </h2>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
        <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
          {dashboards.map((dashboard) => (
            <DashboardRow
              key={dashboard.id}
              dashboard={dashboard}
              isHome={dashboard.id === homeDashboardId}
              isOwned={isOwned}
              onNavigate={() => onNavigate(dashboard)}
              onSetHome={() => onSetHome(dashboard)}
              onDuplicate={() => onDuplicate(dashboard)}
              onDelete={onDelete ? () => onDelete(dashboard) : undefined}
              onToggleShare={onToggleShare ? () => onToggleShare(dashboard) : undefined}
              onRename={onRename ? () => onRename(dashboard) : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface DashboardRowProps {
  dashboard: DashboardsDashboardSummary;
  isHome: boolean;
  isOwned: boolean;
  onNavigate: () => void;
  onSetHome: () => void;
  onDuplicate: () => void;
  onDelete?: () => void;
  onToggleShare?: () => void;
  onRename?: () => void;
}

function DashboardRow({
  dashboard,
  isHome,
  isOwned,
  onNavigate,
  onSetHome,
  onDuplicate,
  onDelete,
  onToggleShare,
  onRename,
}: DashboardRowProps) {
  const menuItems = [];

  if (!isHome) {
    menuItems.push({
      value: 'set-home',
      label: 'Set as home',
      icon: <Home className="h-4 w-4" />,
    });
  }

  menuItems.push({
    value: 'duplicate',
    label: 'Duplicate',
    icon: <Copy className="h-4 w-4" />,
  });

  if (isOwned && onRename) {
    menuItems.push({
      value: 'rename',
      label: 'Rename',
      icon: <Pencil className="h-4 w-4" />,
    });
  }

  if (isOwned && onToggleShare) {
    menuItems.push({
      value: 'toggle-share',
      label: dashboard.isShared ? 'Stop sharing' : 'Share',
      icon: dashboard.isShared ? <Lock className="h-4 w-4" /> : <Share2 className="h-4 w-4" />,
    });
  }

  if (isOwned && onDelete) {
    menuItems.push({
      value: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'danger' as const,
    });
  }

  const handleMenuSelect = (value: string) => {
    switch (value) {
      case 'set-home':
        onSetHome();
        break;
      case 'duplicate':
        onDuplicate();
        break;
      case 'rename':
        onRename?.();
        break;
      case 'toggle-share':
        onToggleShare?.();
        break;
      case 'delete':
        onDelete?.();
        break;
    }
  };

  return (
    <div
      className={`group flex items-center gap-4 px-4 py-3 transition-colors ${
        isHome
          ? 'border-l-2 border-l-blue-500 bg-blue-50/50 dark:bg-blue-500/5'
          : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.02]'
      }`}
    >
      {/* Clickable area */}
      <div
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-4"
        onClick={onNavigate}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onNavigate();
          }
        }}
      >
        {/* Icon */}
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
            isHome
              ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'
              : 'bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-400'
          }`}
        >
          <LayoutDashboard className="h-5 w-5" />
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
              {dashboard.name}
            </span>
            {isHome && (
              <Badge variant="info" size="sm">
                Home
              </Badge>
            )}
            {dashboard.isShared && isOwned && (
              <Badge variant="default" size="sm">
                Shared
              </Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-gray-500 dark:text-zinc-500">
            {dashboard.description || `${dashboard.widgetCount ?? 0} widgets`}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
        <Dropdown
          trigger={
            <IconButton icon={<MoreVertical className="h-4 w-4" />} label="Dashboard actions" />
          }
          align="right"
          onSelect={handleMenuSelect}
          items={menuItems}
        />
      </div>
    </div>
  );
}
